/**
 * Progressive renderer for a streaming SVG whose closing </svg> has not
 * yet arrived. Port of mobile _ProgressiveSvg (message_bubble.dart):
 *   - Truncate to the last '>' to drop a half-written tag.
 *   - Synthesise </svg> and try to parse via DOMParser every ~250ms as
 *     deltas land.
 *   - Hold the last-good HTML between failed parses so the view never
 *     blanks mid-stream.
 */
import React, { useEffect, useRef } from 'react';

const DEBOUNCE_MS = 250;
const SHADOW_STYLE = '<style>:host{display:block;max-height:500px;line-height:0;color:var(--text-primary);}svg{max-width:100%;height:auto;max-height:500px;}</style>';

const tryClose = (partial) => {
  const lastGt = partial.lastIndexOf('>');
  const safe = lastGt >= 0 ? partial.slice(0, lastGt + 1) : partial;
  if (!safe) return null;
  const candidate = `${safe}</svg>`;
  try {
    const doc = new DOMParser().parseFromString(candidate, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return null;
    const svgEl = doc.documentElement;
    if (!svgEl || svgEl.tagName.toLowerCase() !== 'svg') return null;
    return svgEl.outerHTML;
  } catch {
    return null;
  }
};

const ProgressiveSvg = ({ partial }) => {
  const hostRef = useRef(null);
  const lastGoodRef = useRef(null);
  const lastAttemptRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
    const paint = () => {
      lastAttemptRef.current = Date.now();
      const html = tryClose(partial);
      if (html) {
        lastGoodRef.current = html;
        root.innerHTML = `${SHADOW_STYLE}${html}`;
      } else if (lastGoodRef.current && !root.querySelector('svg')) {
        root.innerHTML = `${SHADOW_STYLE}${lastGoodRef.current}`;
      }
    };
    const sinceLast = Date.now() - lastAttemptRef.current;
    if (sinceLast >= DEBOUNCE_MS) {
      paint();
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(paint, DEBOUNCE_MS - sinceLast);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [partial]);

  return (
    <div
      ref={hostRef}
      className="inline-svg-wrapper not-prose"
      style={{ margin: '8px 0', minHeight: 40 }}
    />
  );
};

export default React.memo(ProgressiveSvg);
